/*
 * Behavior: keystroke timing looks synthetic. Records the time of each keydown
 * and runs the analysis in lib/bots/keyboardBehavior, reporting if it looks
 * bot-like. Paste detection lives in usePasteBehaviorBotCheck. Reports via the
 * shared callback from useBotDetection.
 */
import { useEffect, useRef } from 'react'
import { analyzeKeyboardBehavior } from 'lib/bots/keyboardBehavior'
import { TrackBotTrafficAction } from 'lib/bots/trackBotTraffic'

const useKeyboardBehaviorBotCheck = (report: (action: TrackBotTrafficAction) => void): void => {
  // Time of each keydown, in milliseconds since page load.
  const keydownTimesMs = useRef<number[]>([])
  // The field the recorded keystrokes belong to; a fast run must stay in one field.
  const currentField = useRef<EventTarget | null>(null)

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      // Ignore auto-repeat from a physically held key — it is not a new keystroke.
      if (event.repeat) {
        return
      }

      // Ignore IME / composition keystrokes. Mobile soft keyboards and autocorrect
      // fire these (keyCode 229 or isComposing) in fast batches that are not real
      // per-key timing and would otherwise look impossibly fast.
      if (event.isComposing || event.keyCode === 229) {
        return
      }

      // Typing moved to a different field: start counting fresh, so a fast run is
      // only ever measured within a single field.
      if (event.target !== currentField.current) {
        keydownTimesMs.current = []
        currentField.current = event.target
      }

      keydownTimesMs.current.push(event.timeStamp)

      if (analyzeKeyboardBehavior(keydownTimesMs.current)) {
        report('keyboard')
        // Drop the recorded times so one detection does not keep re-flagging on
        // stale gaps; the next keystrokes are judged on their own from scratch.
        keydownTimesMs.current = []
      }
    }

    window.addEventListener('keydown', handleKeyDown, { passive: true })

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [])
}

export default useKeyboardBehaviorBotCheck
